import {
	CancellationDate,
	CancellationEndpoint,
	ReasonForCancellation,
} from '@acorn-portal/shared/src/pages/cancellation/types';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	CancellationClient,
	CancellationEligibilityResponse,
	CancellationIneligibilityReason,
	CancellationResponseDto,
	CreateCancellationCommand,
	ProductBrandSupportedResponse,
	RejectCancellationCommand,
	UpdateCancellationCommand,
} from '@acorn-portal/shared/src/services/cancellation-api';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { safelyConsoleError } from '../../utils/safely-console-error';

/** Reusable: API status with optional error (e.g. eligibility, rating). */
export interface ApiStatus {
	isLoading: boolean;
	error: string | undefined;
}
interface CancellationState {
	// * BE Access control (primary source of truth) *
	// 	// Pre-eligibility
	checkedPreEligibility: boolean;
	isPreEligible: boolean;
	preEligibilityStatus: ApiStatus;
	// Eligibility
	checkedEligibility: boolean;
	isEligible: boolean;
	ineligibilityReason: CancellationIneligibilityReason | undefined;
	eligibilityStatus: ApiStatus;
	// * FE access control *
	stepCeiling: CancellationEndpoint | undefined;
	takenUp: boolean;
	// * FE data capture *
	reasonForCancellation: ReasonForCancellation;
	cancellationDate: CancellationDate;
	// * FE data submission *
	// Rating - create/update cancellation quote
	cancellationRatingCommand: CreateCancellationCommand | UpdateCancellationCommand | undefined; // used to detect changes
	cancellationRatingResponse?: CancellationResponseDto;
	ratingStatus: ApiStatus;
	// Reject - cleanup operation on the transaction
	rejectStatus: Partial<ApiStatus>;
}

export const INITIAL_CANCELLATION_STATE: CancellationState = {
	checkedPreEligibility: false,
	isPreEligible: false,
	preEligibilityStatus: { isLoading: false, error: undefined },
	checkedEligibility: false,
	isEligible: false,
	ineligibilityReason: undefined,
	eligibilityStatus: { isLoading: false, error: undefined },
	stepCeiling: undefined,
	takenUp: false,
	reasonForCancellation: '',
	cancellationDate: { description: '', date: '' },
	cancellationRatingCommand: undefined,
	cancellationRatingResponse: undefined,
	ratingStatus: { isLoading: false, error: undefined },
	rejectStatus: { isLoading: false }, // loading only - we fail silently as this is a cleanup operation
};

const apiEndpoint = import.meta.env.VITE_CANCELLATION_API;

/** _Step 1_ Pre-eligibility checks for **BASE** journey access - i.e. can view the journey access point */
// TODO: hook up BE and implement testing
export const getCancellationPreEligibility = createAsyncThunk<
	ProductBrandSupportedResponse,
	undefined,
	{ rejectValue: string; state: RootState }
>('cancellation/getPreEligibility', async (_, thunkAPI) => {
	try {
		const client = new CancellationClient(apiEndpoint, API);
		const rootState = thunkAPI.getState();
		// Limited products have cancellations enabled, so we must pass this in headers
		const xProductHeader = rootState?.fullPolicy?.product;
		const result = await client.isProductAndBrandSupported(xProductHeader, undefined);
		return result;
	} catch (error) {
		const message = 'Error fetching cancellation pre-eligibility';
		safelyConsoleError(message, error);
		return thunkAPI.rejectWithValue(message);
	}
});

/** _Step 2_ Eligibility checks for **FULL** journey access - i.e. check policy to unblock the journey and start the flow */
export const getCancellationEligibility = createAsyncThunk<
	CancellationEligibilityResponse,
	undefined,
	{ rejectValue: string; state: RootState }
>('cancellation/getEligibility', async (_, thunkAPI) => {
	try {
		const client = new CancellationClient(apiEndpoint, API);
		const rootState = thunkAPI.getState();
		// Limited products have cancellations enabled, so we must pass this in headers
		const policyToken = rootState?.fullPolicy?.policyToken;
		const xProductHeader = rootState?.fullPolicy?.product;
		const result = await client.isEligible(policyToken, xProductHeader, undefined);
		return result;
	} catch (error) {
		const message = 'Error fetching cancellation eligibility';
		safelyConsoleError(message, error);
		return thunkAPI.rejectWithValue(message);
	}
});

/** Propose a cancellation (creates transaction) */
export const proposeCancellation = createAsyncThunk<
	CancellationResponseDto,
	CreateCancellationCommand,
	{ rejectValue: string; state: RootState }
>('cancellation/propose', async (command: CreateCancellationCommand, thunkAPI) => {
	try {
		const client = new CancellationClient(apiEndpoint, API);
		const rootState = thunkAPI.getState();
		// Limited products have cancellations enabled, so we must pass this in headers
		const xProductHeader = rootState?.fullPolicy?.product;
		const response = await client.propose(xProductHeader, undefined, command);
		return response;
	} catch (error) {
		const message = 'Error proposing cancellation';
		safelyConsoleError(message, error);
		return thunkAPI.rejectWithValue(message);
	}
});

/** Amend a cancellation (updates transaction for modification) */
export const amendCancellation = createAsyncThunk<
	CancellationResponseDto,
	UpdateCancellationCommand,
	{ rejectValue: string; state: RootState }
>('cancellation/amend', async (command: UpdateCancellationCommand, thunkAPI) => {
	try {
		const client = new CancellationClient(apiEndpoint, API);
		const rootState = thunkAPI.getState();
		// Limited products have cancellations enabled, so we must pass this in headers
		const xProductHeader = rootState?.fullPolicy?.product;
		const response = await client.amend(xProductHeader, undefined, command);
		return response;
	} catch (error) {
		const message = 'Error amending cancellation';
		safelyConsoleError(message, error);
		return thunkAPI.rejectWithValue(message);
	}
});

/** Reject a cancellation (updates transaction for cleanup) */
export const rejectCancellation = createAsyncThunk<
	boolean,
	RejectCancellationCommand,
	{ rejectValue: string; state: RootState }
>('cancellation/reject', async (command: RejectCancellationCommand, thunkAPI) => {
	try {
		const rootState = thunkAPI.getState();
		// Limited products have cancellations enabled, so we must pass this in headers
		const xProductHeader = rootState?.fullPolicy?.product;
		const client = new CancellationClient(apiEndpoint, API);
		const result = await client.reject(xProductHeader, undefined, command);
		return result;
	} catch (error) {
		const message = 'Error rejecting cancellation';
		safelyConsoleError(message, error);
		return thunkAPI.rejectWithValue(message);
	}
});

const cancellationSlice = createSlice({
	name: 'cancellation',
	initialState: INITIAL_CANCELLATION_STATE,
	reducers: {
		updateStepCeiling(state, action: PayloadAction<CancellationEndpoint>) {
			return { ...state, stepCeiling: action.payload };
		},
		updateTakenUp(state, action: PayloadAction<boolean>) {
			return { ...state, takenUp: action.payload };
		},
		updateReasonForCancellation(state, action: PayloadAction<ReasonForCancellation | ''>) {
			return { ...state, reasonForCancellation: action.payload };
		},
		updateCancellationDate(state, action: PayloadAction<CancellationDate>) {
			return { ...state, cancellationDate: action.payload };
		},
		updateCancellationRatingCommand(
			state,
			action: PayloadAction<CreateCancellationCommand | UpdateCancellationCommand | undefined>,
		) {
			return { ...state, cancellationRatingCommand: action.payload };
		},
		updateCancellationRatingResponse(state, action: PayloadAction<CancellationResponseDto | undefined>) {
			return { ...state, cancellationRatingResponse: action.payload };
		},
		/** Clear rating error (e.g. when closing error modal before retry). */
		updateRatingError(state, action: PayloadAction<string | undefined>) {
			state.ratingStatus.error = action.payload;
		},
		resetEligibility(state) {
			state.checkedEligibility = INITIAL_CANCELLATION_STATE.checkedEligibility;
			state.isEligible = INITIAL_CANCELLATION_STATE.isEligible;
			state.ineligibilityReason = INITIAL_CANCELLATION_STATE.ineligibilityReason;
			state.eligibilityStatus = { ...INITIAL_CANCELLATION_STATE.eligibilityStatus };
		},
		resetCancellationData() {
			return INITIAL_CANCELLATION_STATE;
		},
	},
	extraReducers: (builder) => {
		const handleRatingError = (state: CancellationState) => {
			state.ratingStatus.isLoading = false;
			state.stepCeiling = 'date';
			state.cancellationDate = INITIAL_CANCELLATION_STATE.cancellationDate;
			state.cancellationRatingCommand = undefined;
			state.cancellationRatingResponse = undefined;
		};

		// Handle cancellation pre-eligibility
		builder
			.addCase(getCancellationPreEligibility.pending, (state) => {
				state.preEligibilityStatus.isLoading = true;
			})
			.addCase(getCancellationPreEligibility.fulfilled, (state, action) => {
				state.preEligibilityStatus.isLoading = false;
				state.preEligibilityStatus.error = undefined;
				state.checkedPreEligibility = true;
				state.isPreEligible = action.payload.isSupported ?? false;
			})
			.addCase(getCancellationPreEligibility.rejected, (state) => {
				state.preEligibilityStatus.isLoading = false;
				state.preEligibilityStatus.error = 'Failed to fetch cancellation pre-eligibility';
			});

		// Handle propose cancellation (rating)
		builder
			.addCase(proposeCancellation.pending, (state) => {
				state.ratingStatus.isLoading = true;
				state.ratingStatus.error = undefined;
			})
			.addCase(proposeCancellation.fulfilled, (state, action) => {
				state.ratingStatus.isLoading = false;
				state.cancellationRatingResponse = action.payload;
			})
			.addCase(proposeCancellation.rejected, (state) => {
				state.ratingStatus.error = 'Failed to propose cancellation';
				handleRatingError(state);
			});

		// Handle amend cancellation (rating)
		builder
			.addCase(amendCancellation.pending, (state) => {
				state.ratingStatus.isLoading = true;
				state.ratingStatus.error = undefined;
			})
			.addCase(amendCancellation.fulfilled, (state, action) => {
				state.ratingStatus.isLoading = false;
				state.cancellationRatingResponse = action.payload;
			})
			.addCase(amendCancellation.rejected, (state) => {
				state.ratingStatus.error = 'Failed to amend cancellation';
				handleRatingError(state);
			});

		// Handle get cancellation eligibility
		builder
			.addCase(getCancellationEligibility.pending, (state) => {
				state.eligibilityStatus.isLoading = true;
				state.eligibilityStatus.error = undefined;
			})
			.addCase(getCancellationEligibility.fulfilled, (state, action) => {
				state.eligibilityStatus.isLoading = false;
				state.eligibilityStatus.error = undefined;
				state.checkedEligibility = true;
				state.isEligible = action.payload.isEligible ?? false;
				state.ineligibilityReason = action.payload.reason;
			})
			.addCase(getCancellationEligibility.rejected, (state) => {
				state.eligibilityStatus.isLoading = false;
				state.eligibilityStatus.error = 'Failed to fetch cancellation eligibility';
			});

		// Handle reject cancellation (loading only; no error state)
		builder
			.addCase(rejectCancellation.pending, (state) => {
				state.rejectStatus.isLoading = true;
			})
			.addCase(rejectCancellation.fulfilled, () => {
				return INITIAL_CANCELLATION_STATE; // covers the loading status and the overall cancellation state
			})
			.addCase(rejectCancellation.rejected, (state) => {
				state.rejectStatus.isLoading = false;
			});
	},
});

export const {
	updateStepCeiling,
	updateTakenUp,
	updateReasonForCancellation,
	updateCancellationDate,
	updateCancellationRatingCommand,
	updateCancellationRatingResponse,
	updateRatingError,
	resetEligibility,
	resetCancellationData,
} = cancellationSlice.actions;

export default cancellationSlice.reducer;

export type { CancellationState };
